/**
 * Mercado de la comunidad: vocabulary, rules and API shapes shared by the server and the site.
 * Buyers pay the seller directly; the shop never holds or takes money.
 */
import type { PublicCoupon } from "./marketCoupons";
import { RATED_ZONES, type RatedZone } from "./shipping";
import { VARIANT_LANGUAGES } from "./variants";
import { GAME_PARAM, gameSlug } from "./games";

export const MARKET_CONDITIONS = ["NM", "LP", "MP", "HP", "DMG"] as const;
export type MarketCondition = (typeof MARKET_CONDITIONS)[number];
/** Single cards use MARKET_CONDITIONS; sealed products are always "SEALED". */
export type ListingCondition = MarketCondition | "SEALED";

/** Catalog categories sellers can't list (everything else: singles and sealed products). */
export const MARKET_EXCLUDED_CATEGORIES = ["accesorios"];
/** Sealed product (anything but a single); unknown counts as a single, the safer default for forms. */
export const isSealedCategory = (category: string | null | undefined) => typeof category === "string" && category !== "" && category !== "singles";

export const MARKET_FINISHES = ["normal", "holo", "reverse", "first_edition"] as const;
export type MarketFinish = (typeof MARKET_FINISHES)[number];
export const GRADERS = ["PSA", "BGS", "CGC", "SGC", "TAG", "ACE"] as const;
export type Grader = (typeof GRADERS)[number];
/** How a grader shows in the forms: BGS is better known as Beckett. Chips keep the short name ("BGS 9.5"). */
export const graderLabel = (g: Grader): string => (g === "BGS" ? "BGS (Beckett)" : g);
export const MARKET_LANGUAGES = VARIANT_LANGUAGES;

export const MARKET_PAYMENT_METHODS = ["mercadopago", "transfer", "crypto", "cash"] as const;
export type MarketPaymentMethod = (typeof MARKET_PAYMENT_METHODS)[number];
export const DELIVERY_MODES = ["hand", "ship", "pickup", "arrange"] as const;
export type DeliveryMode = (typeof DELIVERY_MODES)[number];
/** Handed over in person: these can be paid in cash and get a meeting day. */
export const IN_PERSON_MODES: readonly DeliveryMode[] = ["hand", "pickup", "arrange"];
export const isInPerson = (m: DeliveryMode) => IN_PERSON_MODES.includes(m);

export const SELLER_STATUSES = ["pending", "active", "rejected", "paused", "banned"] as const;
/** A person (verification photo and birth date) or a company (legal name and CUIT, db 0076). */
export const SELLER_KINDS = ["person", "company"] as const;
export type SellerKind = (typeof SELLER_KINDS)[number];

/**
 * A company's CUIT (30, 33 or 34: legal persons) as its 11 digits, check digit verified; null when it
 * isn't one. Sole traders' CUITs (20, 23, 24, 27) are people's: they apply as a person.
 */
export function companyCuit(raw: unknown): string | null {
  const d = typeof raw === "string" ? raw.replace(/[\s.-]/g, "") : "";
  if (!/^(30|33|34)\d{9}$/.test(d)) return null;
  const sum = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2].reduce((n, w, i) => n + w * Number(d[i]), 0);
  const check = (11 - (sum % 11)) % 11;
  return check !== 10 && check === Number(d[10]) ? d : null;
}

/** "30-71234567-1". */
export const formatCuit = (d: string) => `${d.slice(0, 2)}-${d.slice(2, 10)}-${d.slice(10)}`;
export type SellerStatus = (typeof SELLER_STATUSES)[number];
export const ORDER_STATUSES = ["pending_payment", "confirmed", "paid", "shipped", "completed", "cancelled", "disputed"] as const;
export type MarketOrderStatus = (typeof ORDER_STATUSES)[number];
export const OFFER_STATUSES = ["pending", "countered", "accepted", "rejected", "cancelled", "expired", "used"] as const;
export type OfferStatus = (typeof OFFER_STATUSES)[number];
export const REPORT_REASONS = ["fake", "wrong_item", "not_delivered", "not_paid", "abuse", "spam", "other"] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];
export const REPORT_TARGETS = ["listing", "seller", "order", "review", "want"] as const;
export type ReportTarget = (typeof REPORT_TARGETS)[number];

/** Unpaid orders give the cards back after this (Mercado Pago / transfer and crypto). */
export const MP_RESERVATION_MINUTES = 60;
export const MANUAL_RESERVATION_MINUTES = 120;
/** Shipped orders complete on their own if the buyer says nothing. */
export const AUTO_COMPLETE_DAYS = 10;
/** Offers (and accepted prices) last this long. */
export const OFFER_HOURS = 48;
export const MAX_PHOTOS = 2;
export const MAX_ORDER_LINES = 30;
/** New sellers (under NEW_SELLER_SALES completed sales) can have at most this many active listings. */
export const NEW_SELLER_SALES = 5;
/** A seller is new for this many days after approval (and until NEW_SELLER_SALES sales), unless the admin vouches for them. */
export const NEW_SELLER_DAYS = 30;
/** New sellers can't take bank transfers or crypto (no buyer protection) for orders above this: Mercado Pago or cash in hand only. */
export const NEW_SELLER_SAFE_PAY_ABOVE_ARS = 50_000;
/** Payment methods a new seller can take above NEW_SELLER_SAFE_PAY_ABOVE_ARS. */
export const SAFE_PAYMENTS: readonly MarketPaymentMethod[] = ["mercadopago", "cash"];
/** The highest minimum purchase a shop can set (pesos). */
export const MIN_ORDER_MAX_ARS = 10_000_000;
/** A shop's minimum purchase as its settings send it: whole pesos, null for none ("", 0 or null); undefined when it isn't valid. */
export function parseMinOrder(v: unknown): number | null | undefined {
  if (v === null || (typeof v === "string" && v.trim() === "")) return null;
  const n = typeof v === "string" ? Number(v) : v;
  if (n === 0) return null;
  return typeof n === "number" && Number.isInteger(n) && n > 0 && n <= MIN_ORDER_MAX_ARS ? n : undefined;
}

/** New seller: approved less than NEW_SELLER_DAYS ago or under NEW_SELLER_SALES completed sales, and not vouched for by the admin (NEW_SELLER_SQL agrees). */
export function isNewSeller(s: { sales: number; approvedAt: Date | string | null; vettedAt: Date | string | null }, now = new Date()): boolean {
  if (s.vettedAt) return false;
  if (s.sales < NEW_SELLER_SALES || !s.approvedAt) return true;
  return now.getTime() - new Date(s.approvedAt).getTime() < NEW_SELLER_DAYS * 86_400_000;
}

/** Listings a shop under review can prepare (they go live on approval, up to its plan's cap). */
export const PENDING_LISTINGS = 200;

/** Seller plans: BASIC (everyone), EX and VMAX (paid; given by hand from the admin for now; founders get VMAX free). */
export const SELLER_PLANS = ["basic", "ex", "vmax"] as const;
export type SellerPlan = (typeof SELLER_PLANS)[number];
export const PAID_PLANS = ["ex", "vmax"] as const;
export type PaidPlan = (typeof PAID_PLANS)[number];
export const PLAN_SOURCES = ["founder", "manual", "mp_subscription"] as const;
export type PlanSource = (typeof PLAN_SOURCES)[number];
export const PLAN_LABEL: Record<SellerPlan, string> = { basic: "BASIC", ex: "EX", vmax: "VMAX" };
/** What each plan allows: live listings and the highest price per listing (null: no limit). */
export const PLAN_LIMITS: Record<SellerPlan, { listings: number | null; maxPriceArs: number | null }> = {
  basic: { listings: 10, maxPriceArs: 100_000 },
  ex: { listings: 20, maxPriceArs: 300_000 },
  vmax: { listings: null, maxPriceArs: null },
};
/** What a shop coupon applies to: the whole shop, some products, a set, a group of customers or one person. */
export const COUPON_SCOPES = ["shop", "products", "set", "segment", "user"] as const;
export type CouponScope = (typeof COUPON_SCOPES)[number];
/** What else each plan includes (server gating, the comparison table and "Disponible en VMAX" all read this). */
export const PLAN_PERKS: Record<
  SellerPlan,
  { featuredPerMonth: number; demandAlerts: boolean; customShop: boolean; prioritySupport: boolean; insights: boolean; coupons: number; couponScopes: readonly CouponScope[]; customers: boolean; shopEmailsPerMonth: number; performance: boolean; quickReplies: number; shopPins: number; shopSections: number; reservations: number; usdPrices: boolean; api: boolean; apiKeys: number; webhooks: number }
> = {
  basic: { featuredPerMonth: 0, demandAlerts: false, customShop: false, prioritySupport: false, insights: false, coupons: 0, couponScopes: [], customers: false, shopEmailsPerMonth: 0, performance: false, quickReplies: 0, shopPins: 0, shopSections: 0, reservations: 0, usdPrices: false, api: false, apiKeys: 0, webhooks: 0 },
  ex: { featuredPerMonth: 3, demandAlerts: false, customShop: false, prioritySupport: false, insights: true, coupons: 1, couponScopes: ["shop"], customers: false, shopEmailsPerMonth: 0, performance: false, quickReplies: 5, shopPins: 0, shopSections: 0, reservations: 0, usdPrices: false, api: false, apiKeys: 0, webhooks: 0 },
  vmax: { featuredPerMonth: 10, demandAlerts: true, customShop: true, prioritySupport: true, insights: true, coupons: 10, couponScopes: COUPON_SCOPES, customers: true, shopEmailsPerMonth: 4, performance: true, quickReplies: 20, shopPins: 8, shopSections: 3, reservations: 10, usdPrices: true, api: true, apiKeys: 5, webhooks: 5 },
};
/** The cheapest plan with coupons, or with a coupon scope. */
export const planWithCoupons = (scope: CouponScope = "shop"): SellerPlan => SELLER_PLANS.find((p) => PLAN_PERKS[p].couponScopes.includes(scope))!;
/** A featured listing stays featured this long; the strip over the market's results shows at most FEATURED_STRIP, one per shop. */
export const FEATURE_DAYS = 7;
export const FEATURED_STRIP = 4;
/** The cheapest plan that includes a perk ("Disponible en VMAX"). */
export const planWith = (perk: "demandAlerts" | "customShop" | "prioritySupport" | "insights" | "customers" | "performance" | "usdPrices" | "api"): SellerPlan => SELLER_PLANS.find((p) => PLAN_PERKS[p][perk])!;

/** Customized shop (VMAX): links only to known social networks (never chats or payment pages). */
export const SHOP_LINK_HOSTS: Record<string, string> = {
  "instagram.com": "Instagram",
  "tiktok.com": "TikTok",
  "youtube.com": "YouTube",
  "youtu.be": "YouTube",
  "facebook.com": "Facebook",
  "x.com": "X",
  "twitter.com": "X",
  "twitch.tv": "Twitch",
  "kick.com": "Kick",
  "threads.net": "Threads",
};
export const SHOP_ABOUT_MAX = 3000;
export const SHOP_LINKS_MAX = 4;
export const SHOP_TAGLINE_MAX = 80;
export interface ShopLink {
  url: string;
  label: string;
}

/**
 * The shop's color, from a fixed palette (no free picker): each one takes white text at 4.5:1 or more,
 * so a button in it always reads. Used only on the shop's own page.
 */
export const SHOP_ACCENTS = {
  rojo: "#c62828",
  naranja: "#c2410c",
  dorado: "#a16207",
  verde: "#15803d",
  turquesa: "#0f766e",
  celeste: "#0e7490",
  azul: "#1d4ed8",
  indigo: "#4338ca",
  violeta: "#6d28d9",
  rosa: "#be185d",
  marron: "#7c4a1e",
  grafito: "#334155",
} as const;
export type ShopAccent = keyof typeof SHOP_ACCENTS;
export const isShopAccent = (v: unknown): v is ShopAccent => typeof v === "string" && Object.hasOwn(SHOP_ACCENTS, v);

export const SHOP_ANNOUNCEMENT_MAX = 120;
/** Coupons a shop can announce: the public ones (never a group's or a person's). */
export const ANNOUNCEABLE_SCOPES = ["shop", "products", "set"] as const;
export type AnnounceableScope = (typeof ANNOUNCEABLE_SCOPES)[number];

/** The announcement as its seller edits it. */
export interface ShopAnnouncementInput {
  text: string;
  couponId: number | null;
  /** Last day it shows (Argentina date), or null. */
  until: string | null;
}

/** The announcement over a shop's page: only while its day and its coupon still hold. */
export interface ShopAnnouncement {
  text: string;
  until: string | null;
  coupon: (PublicCoupon & { scope: AnnounceableScope; setName: string | null }) | null;
}

/**
 * The shop's own sections (VMAX): a template each, so a seller picks, never builds a query.
 * sealed: sealed products · newest: listed in the last SHOP_SECTION_NEWEST_DAYS · under: up to a price ·
 * graded · set: one set · coupon: cards a live public coupon of the shop covers (by cards or set) ·
 * manual: picked by hand, in order.
 */
export const SHOP_SECTION_TYPES = ["sealed", "newest", "under", "graded", "set", "coupon", "manual"] as const;
export type ShopSectionType = (typeof SHOP_SECTION_TYPES)[number];
export const SHOP_SECTIONS_MAX = 3;
export const SHOP_SECTION_TITLE_MAX = 40;
/** Cards a section shows on the page; a hand-picked one holds at most this many. */
export const SHOP_SECTION_ITEMS = 12;
export const SHOP_SECTION_NEWEST_DAYS = 14;

export interface ShopSection {
  title: string;
  type: ShopSectionType;
  /** under */
  maxPriceArs?: number;
  /** set */
  setName?: string;
  /** manual, in order */
  listingIds?: number[];
}

/** Where the shop's banner leads: one of its sections (by position) or one of its own listings. */
export type ShopBannerLink = { kind: "section"; index: number } | { kind: "listing"; id: number };

/** A banner link as a seller sends it: null for none, undefined when it's not one. */
export function parseBannerLink(raw: unknown): ShopBannerLink | null | undefined {
  if (raw === null) return null;
  const r = raw as Record<string, unknown> | undefined;
  if (r?.kind === "section" && Number.isInteger(r.index) && (r.index as number) >= 0 && (r.index as number) < SHOP_SECTIONS_MAX) return { kind: "section", index: r.index as number };
  if (r?.kind === "listing" && Number.isInteger(r.id) && (r.id as number) > 0) return { kind: "listing", id: r.id as number };
  return undefined;
}

/**
 * Hand-picked sections left with no listings go; `moved(i)` is where section i ended up (-1: gone),
 * so a banner pointing at a section keeps pointing at the same one.
 */
export function dropEmptySections(sections: ShopSection[]): { kept: ShopSection[]; moved: (i: number) => number } {
  const survivors = sections.map((x, i) => ({ x, i })).filter(({ x }) => !x.listingIds || x.listingIds.length > 0);
  return { kept: survivors.map(({ x }) => x), moved: (i) => survivors.findIndex((s) => s.i === i) };
}

/** A banner link after the sections moved: the same section at its new place, or none when it went. */
export function movedBannerLink(link: ShopBannerLink | null, moved: (i: number) => number): ShopBannerLink | null {
  if (link?.kind !== "section") return link;
  const index = moved(link.index);
  return index < 0 ? null : { kind: "section", index };
}

/** Where the banner leads on the page, checked: a section shown now (its position among them) or a listing on sale. */
export type ShopBannerTarget = { kind: "section"; index: number; title: string } | { kind: "listing"; id: number; name: string };

/** A section as the page shows it: its first cards and how many match in all. */
export interface ShopSectionView extends ShopSection {
  items: ListingCard[];
  total: number;
}

/** Sections as a seller sends them (null: something's wrong). Ownership of hand-picked listings is the server's check. */
export function parseShopSections(raw: unknown): ShopSection[] | null {
  if (!Array.isArray(raw) || raw.length > SHOP_SECTIONS_MAX) return null;
  const out: ShopSection[] = [];
  for (const r of raw as Record<string, unknown>[]) {
    const title = typeof r?.title === "string" ? r.title.replace(/\s+/g, " ").trim() : "";
    const type = r?.type;
    if (title.length < 2 || title.length > SHOP_SECTION_TITLE_MAX || !(SHOP_SECTION_TYPES as readonly unknown[]).includes(type)) return null;
    const section: ShopSection = { title, type: type as ShopSectionType };
    if (type === "under") {
      const n = Number(r.maxPriceArs);
      if (!Number.isInteger(n) || n < 1 || n > 100_000_000) return null;
      section.maxPriceArs = n;
    }
    if (type === "set") {
      const set = typeof r.setName === "string" ? r.setName.trim() : "";
      if (!set || set.length > 80) return null;
      section.setName = set;
    }
    if (type === "manual") {
      const ids = r.listingIds;
      if (!Array.isArray(ids) || !ids.length || ids.length > SHOP_SECTION_ITEMS || !ids.every((id) => Number.isInteger(id) && id > 0) || new Set(ids).size !== ids.length) return null;
      section.listingIds = ids as number[];
    }
    out.push(section);
  }
  return out;
}

/** "Reservar para un cliente": how long the card is set aside, and the note's length. */
export const RESERVATION_HOURS = 48;
export const RESERVATION_NOTE_MAX = 140;
export type ReservationStatus = "open" | "claimed" | "used" | "cancelled" | "expired";

/** A reservation in the seller's list. */
export interface SellerReservation {
  id: number;
  token: string;
  listingId: number;
  listingName: string;
  quantity: number;
  priceArs: number;
  note: string;
  status: ReservationStatus;
  /** Who took it (claimed or used). */
  buyerName: string | null;
  orderNumber: string | null;
  expiresAt: string;
  createdAt: string;
}

/** The reservation's page as the link's visitor sees it: the card and the price, or just "not available". */
export type ReservationPage =
  | { available: false }
  | {
      available: true;
      /** Already taken by the visitor: go on to pay. */
      mine: boolean;
      listing: ListingCard;
      quantity: number;
      priceArs: number;
      note: string;
      expiresAt: string;
      /** Set once taken by the visitor: the accepted offer the checkout uses. */
      offerId: number | null;
    };

/** The customized shop's content (VMAX): what its page shows while the plan includes it. */
export interface ShopContent {
  banner: string | null;
  about: string;
  links: ShopLink[];
  /** Square logo, in place of the avatar on the shop's page. */
  logo: string | null;
  /** One line under the shop's name. */
  tagline: string;
  accent: ShopAccent | null;
}

/** A link to a known social network (host or a real subdomain of it), normalized; null otherwise. */
export function parseShopLink(raw: unknown): ShopLink | null {
  const url = normalizeSocialUrl(raw);
  if (!url) return null;
  const u = new URL(url);
  if (u.protocol !== "https:") u.protocol = "https:";
  const host = u.hostname.toLowerCase();
  const known = Object.keys(SHOP_LINK_HOSTS).find((h) => host === h || host.endsWith(`.${h}`));
  if (!known || !u.pathname.replace(/\/+$/, "")) return null;
  // The networks' own redirectors ("l.facebook.com/l.php?u=…", "youtube.com/redirect?q=…") lead anywhere.
  if (/^(l|lm)\./.test(host) || /\/(l\.php|redirect|linkshim|link|url|out)(\/|$)/i.test(u.pathname)) return null;
  if ([...u.searchParams.values()].some((v) => /:\/\/|^(wa\.me|api\.whatsapp|mpago\.|link\.mercadopago)/i.test(v))) return null;
  return { url: u.href, label: SHOP_LINK_HOSTS[known] };
}

/** A seller's Mercado Pago subscription to a paid plan, as Mi tienda shows it. */
export interface SubscriptionView {
  plan: PaidPlan;
  status: "pending" | "authorized" | "paused" | "cancelled";
  amountArs: number;
  nextPaymentAt: string | null;
  /** While pending: where to finish it in Mercado Pago. */
  url: string | null;
}

/** Monthly prices (shown, with "Suscribirme", once the owner turns plan subscriptions on). */
export const PLAN_PRICE_ARS: Record<PaidPlan, number> = { ex: 9_900, vmax: 49_900 };
/** Founders get this plan free for FOUNDER_PLAN_MONTHS from the launch of paid plans. */
export const FOUNDER_PLAN: PaidPlan = "vmax";
export const FOUNDER_PLAN_MONTHS = 6;
/** Sellers on a paid plan are warned this many days before it ends. */
export const PLAN_ENDING_NOTICE_DAYS = 7;
/** Over a cap: "no limit" (null) is never exceeded. */
export const overCap = (n: number, cap: number | null) => cap !== null && n > cap;
export const atCap = (n: number, cap: number | null) => cap !== null && n >= cap;

/** The plan in force: a paid plan while plan_until is unset or ahead, else BASIC (EFFECTIVE_PLAN_SQL agrees). */
export function effectivePlan(s: { plan: string; planUntil: Date | string | null }, now = new Date()): SellerPlan {
  const paid = (PAID_PLANS as readonly string[]).includes(s.plan);
  return paid && (s.planUntil === null || new Date(s.planUntil).getTime() > now.getTime()) ? (s.plan as SellerPlan) : "basic";
}
export const MARKET_PAGE_SIZE = 24;

export const HANDLE_RE = /^[a-z0-9_]{3,20}$/;
/**
 * Header with which the admin works from inside a shop it set up (Admin › Mercado › "Editar tienda"):
 * the shop's user id. Only the shop panel's routes take it; other writes carrying it are refused.
 */
export const ACT_AS_HEADER = "X-Act-As-Shop";
/** API paths whose routes may take ACT_AS_HEADER (each route still opts in). */
export const ACT_AS_PATHS = ["/api/me/seller", "/api/me/listings", "/api/me/price-ref/"];

export const RESERVED_HANDLES = new Set(["admin", "pokefans", "tienda", "soporte", "mercado", "vendedor", "vendedores", "root", "staff", "oficial"]);
export const isHandle = (h: unknown): h is string => typeof h === "string" && HANDLE_RE.test(h) && !RESERVED_HANDLES.has(h);

export const isOneOf = <T extends string>(list: readonly T[], v: unknown): v is T => typeof v === "string" && (list as readonly string[]).includes(v);

export interface TransferInfo {
  holder: string;
  taxId: string;
  bank: string;
  cbu: string;
  alias: string;
}
export interface WalletInfo {
  asset: string;
  network: string;
  address: string;
}

/** The networks a shop's USDT wallet can be on (the names are stored as they are). */
export const CRYPTO_NETWORKS = ["TRON (TRC-20)", "Ethereum (ERC-20)", "BNB Chain (BEP-20)", "Polygon", "Solana", "Binance Pay"] as const;

/** Each on-chain network's transaction hash format and explorer page; Binance Pay is not on a chain. */
const CHAINS: Record<string, { pattern: RegExp; normalize: (tx: string) => string; explorer: string }> = {
  "TRON (TRC-20)": { pattern: /^(0x)?[0-9a-f]{64}$/i, normalize: (tx) => tx.toLowerCase().replace(/^0x/, ""), explorer: "https://tronscan.org/#/transaction/" },
  "Ethereum (ERC-20)": { pattern: /^(0x)?[0-9a-f]{64}$/i, normalize: (tx) => `0x${tx.toLowerCase().replace(/^0x/, "")}`, explorer: "https://etherscan.io/tx/" },
  "BNB Chain (BEP-20)": { pattern: /^(0x)?[0-9a-f]{64}$/i, normalize: (tx) => `0x${tx.toLowerCase().replace(/^0x/, "")}`, explorer: "https://bscscan.com/tx/" },
  Polygon: { pattern: /^(0x)?[0-9a-f]{64}$/i, normalize: (tx) => `0x${tx.toLowerCase().replace(/^0x/, "")}`, explorer: "https://polygonscan.com/tx/" },
  // Base58 signatures are case-sensitive: kept as they are.
  Solana: { pattern: /^[1-9A-HJ-NP-Za-km-z]{64,90}$/, normalize: (tx) => tx, explorer: "https://solscan.io/tx/" },
};

/**
 * A TRON transaction against its order: ok (confirmed, the exact amount to the order's wallet, after
 * the order), pending (not confirmed or not on the chain yet), not_found (still not there an hour
 * later), or why it isn't this order's payment.
 */
export const TX_CHECKS = ["ok", "pending", "not_found", "wrong_token", "wrong_to", "wrong_amount", "too_old", "used"] as const;
export type TxCheck = (typeof TX_CHECKS)[number];

/** Micro-USDT (6 decimals) as the amount to send: 13340173 → "13.340173". */
export function usdtAmount(units: bigint | number | string): string {
  const u = BigInt(units);
  return `${u / 1_000_000n}.${(u % 1_000_000n).toString().padStart(6, "0")}`;
}

/** Whether a payment on this network has a transaction hash to give. */
export const hasTxHash = (network: string): boolean => network in CHAINS;

/** The hash in its stored form, or null when it isn't one for this network. */
export function cryptoTx(network: string, raw: string): string | null {
  const chain = CHAINS[network];
  const tx = raw.trim();
  return chain && chain.pattern.test(tx) ? chain.normalize(tx) : null;
}

export function txExplorerUrl(network: string, tx: string): string | null {
  const chain = CHAINS[network];
  return chain ? chain.explorer + tx : null;
}
/** One weekday (0 = Sunday) the seller can meet, and its time window ("HH:MM", Argentina time). */
export interface MeetingSlot {
  day: number;
  from: string;
  to: string;
}

/** When the seller can meet: one window per weekday ("Lun 9 a 18, Mar a Jue 10 a 12, Sáb 9 a 17"). */
export interface MeetingSchedule {
  slots: MeetingSlot[];
}

export interface DeliveryOptions {
  /** Hand delivery in these zones (CABA and the GBA rings), at this cost (0 = free). */
  hand: { zones: RatedZone[]; areas: string; costArs: number } | null;
  /** The seller ships; costArs null = quoted in the chat. */
  ship: { carriers: string; costArs: number | null } | null;
  /** A public pickup point. */
  pickup: { address: string; hours: string } | null;
  /** Wherever they agree (in the chat), on one of the schedule's days. */
  arrange: boolean;
  /** Days and time window for in-person deliveries; required for "arrange". */
  schedule: MeetingSchedule | null;
}
export const EMPTY_DELIVERY: DeliveryOptions = { hand: null, ship: null, pickup: null, arrange: false, schedule: null };

const str = (v: unknown, max: number) => (typeof v === "string" ? v.trim().slice(0, max) : "");
const wholeArs = (v: unknown, max = 10_000_000): number | null => {
  const n = typeof v === "string" && v.trim() !== "" ? Number(v) : v;
  return typeof n === "number" && Number.isInteger(n) && n >= 0 && n <= max ? n : null;
};

const TIME_RE = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

const validSlot = (s: Partial<MeetingSlot>): s is MeetingSlot =>
  Number.isInteger(s.day) && s.day! >= 0 && s.day! <= 6 && TIME_RE.test(s.from ?? "") && TIME_RE.test(s.to ?? "") && s.from! < s.to!;

/** Per-day windows; also reads the first format ({ days, from, to }: the same window every day). */
export function parseSchedule(raw: unknown): MeetingSchedule | null {
  const o = (raw && typeof raw === "object" ? raw : null) as Record<string, unknown> | null;
  if (!o) return null;
  const candidates: Partial<MeetingSlot>[] = Array.isArray(o.slots)
    ? o.slots.map((x) => (x && typeof x === "object" ? (x as Partial<MeetingSlot>) : {}))
    : Array.isArray(o.days)
      ? o.days.map((day) => ({ day: day as number, from: o.from as string, to: o.to as string }))
      : [];
  const byDay = new Map<number, MeetingSlot>();
  for (const c of candidates) if (validSlot(c) && !byDay.has(c.day)) byDay.set(c.day, { day: c.day, from: c.from, to: c.to });
  const slots = [...byDay.values()].sort((a, b) => a.day - b.day);
  return slots.length ? { slots } : null;
}

/** The window of a "YYYY-MM-DD" day, if the seller meets that weekday. */
export function slotFor(schedule: MeetingSchedule | null, isoDate: string): MeetingSlot | null {
  if (!schedule || !/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return null;
  const day = new Date(`${isoDate}T12:00:00Z`).getUTCDay();
  return schedule.slots.find((s) => s.day === day) ?? null;
}

/**
 * What a seller asked for that can't be saved: in-person options without their details. The
 * settings form shows the same keys next to each option (mkt.set.err.*).
 */
export function deliveryErrors(raw: unknown): string[] {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, any>;
  const errors: string[] = [];
  if (o.hand && !(Array.isArray(o.hand.zones) && o.hand.zones.some((z: unknown) => isOneOf(RATED_ZONES, z)))) errors.push("delivery.hand");
  if (o.pickup && !str(o.pickup.address, 200)) errors.push("delivery.pickup");
  const rawSlots = Array.isArray(o.schedule?.slots) ? (o.schedule.slots as Partial<MeetingSlot>[]) : [];
  if (rawSlots.some((s) => !validSlot(s ?? {}))) errors.push("delivery.schedule");
  if (o.arrange === true && !parseSchedule(o.schedule)) errors.push("delivery.arrange");
  return errors;
}

export function parseDelivery(raw: unknown): DeliveryOptions {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, any>;
  const schedule = parseSchedule(o.schedule);
  const zones = Array.isArray(o.hand?.zones) ? [...new Set(o.hand.zones.filter((z: unknown) => isOneOf(RATED_ZONES, z)))] as RatedZone[] : [];
  return {
    hand: o.hand && zones.length ? { zones, areas: str(o.hand.areas, 200), costArs: wholeArs(o.hand.costArs) ?? 0 } : null,
    ship: o.ship ? { carriers: str(o.ship.carriers, 120), costArs: o.ship.costArs === null || o.ship.costArs === "" ? null : wholeArs(o.ship.costArs) } : null,
    pickup: o.pickup && str(o.pickup.address, 200) ? { address: str(o.pickup.address, 200), hours: str(o.pickup.hours, 120) } : null,
    // Arranged meetings need at least one day with its time window.
    arrange: o.arrange === true && schedule !== null,
    schedule,
  };
}

/** "YYYY-MM-DD" of `d` in Argentina (UTC-3, no DST). */
export function argentinaDate(d: Date): string {
  return new Date(d.getTime() - 3 * 3_600_000).toISOString().slice(0, 10);
}

/** Sellers must be at least this old. */
export const SELLER_MIN_AGE = 18;

/**
 * The social network or shop link of a seller's request ("instagram.com/x" → "https://instagram.com/x"):
 * http(s) only, with a real host, up to 300 characters; null when it isn't one.
 */
export function normalizeSocialUrl(raw: unknown): string | null {
  const text = typeof raw === "string" ? raw.trim() : "";
  if (!text || text.length > 300 || /\s/.test(text)) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(text) ? text : `https://${text.replace(/^\/+/, "")}`;
  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return null;
  }
  if ((url.protocol !== "https:" && url.protocol !== "http:") || url.username || url.password || !/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(url.hostname)) return null;
  return url.href.length <= 300 ? url.href : null;
}

/** What the market blocklist holds (and what the admin checks for repeats across sellers). */
export const BLOCK_KINDS = ["dni", "cbu", "alias", "phone", "email", "mp_user", "social"] as const;
export type BlockKind = (typeof BLOCK_KINDS)[number];
export const BLOCK_KIND_LABEL: Record<BlockKind, string> = {
  dni: "DNI / CUIT",
  cbu: "CBU / CVU",
  alias: "Alias",
  phone: "Teléfono",
  email: "Email",
  mp_user: "Cuenta de Mercado Pago",
  social: "Red social",
};

/** Warnings about a seller for the admin (and Telegram): data shared with other sellers, blocklisted, or a bank account in someone else's name. */
export type SellerFlag =
  | { type: "duplicate"; kind: BlockKind; handles: string[] }
  | { type: "blocked"; kind: BlockKind; reason: string }
  | { type: "holder"; holder: string; name: string };

export function sellerFlagText(f: SellerFlag): string {
  if (f.type === "duplicate") return `${BLOCK_KIND_LABEL[f.kind]} repetido con ${f.handles.map((h) => `@${h}`).join(", ")}`;
  if (f.type === "blocked") return `${BLOCK_KIND_LABEL[f.kind]} en la lista negra${f.reason ? ` (${f.reason})` : ""}`;
  return `El titular de la cuenta bancaria (${f.holder}) no coincide con el nombre de la cuenta (${f.name})`;
}

/** A real calendar date "YYYY-MM-DD" (no 2026-02-30), from 1900 on. */
export function isIsoDate(s: unknown): s is string {
  if (typeof s !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(s) || s < "1900-01-01") return false;
  const d = new Date(`${s}T12:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

/** Whole years from `birth` to `today` (both "YYYY-MM-DD"). A Feb 29 birthday counts from Mar 1. */
export function ageOn(birth: string, today: string): number {
  const years = Number(today.slice(0, 4)) - Number(birth.slice(0, 4));
  return today.slice(5) >= birth.slice(5) ? years : years - 1;
}

/** Latest birth date that is already an adult today in Argentina ("YYYY-MM-DD"), for the form's max. */
export function adultBornBy(today: string): string {
  const date = `${Number(today.slice(0, 4)) - SELLER_MIN_AGE}${today.slice(4)}`;
  // Feb 29 that year didn't exist: Feb 28 is the latest real date.
  const feb28 = date.replace(/-02-29$/, "-02-28");
  return isIsoDate(date) ? date : feb28;
}

/** Meeting days the buyer can pick: the schedule's weekdays from tomorrow, for `span` days. */
export function meetingDates(schedule: MeetingSchedule | null, now = new Date(), span = 14): string[] {
  if (!schedule) return [];
  const today = argentinaDate(now);
  const out: string[] = [];
  for (let i = 1; i <= span; i++) {
    const day = new Date(`${today}T12:00:00Z`);
    day.setUTCDate(day.getUTCDate() + i);
    if (schedule.slots.some((s) => s.day === day.getUTCDay())) out.push(day.toISOString().slice(0, 10));
  }
  return out;
}

export function deliveryModesOf(d: DeliveryOptions): DeliveryMode[] {
  return DELIVERY_MODES.filter((m) => (m === "arrange" ? d.arrange : d[m] !== null));
}

/** Delivery cost for an order: null when it is agreed later (shipping quoted, pickup/arrange free). */
export function deliveryCost(d: DeliveryOptions, mode: DeliveryMode): number | null {
  if (mode === "hand") return d.hand?.costArs ?? 0;
  if (mode === "ship") return d.ship?.costArs ?? null;
  return mode === "pickup" ? 0 : null;
}

export const WEEKDAY_LABEL: { es: string; en: string }[] = [
  { es: "Domingo", en: "Sunday" },
  { es: "Lunes", en: "Monday" },
  { es: "Martes", en: "Tuesday" },
  { es: "Miércoles", en: "Wednesday" },
  { es: "Jueves", en: "Thursday" },
  { es: "Viernes", en: "Friday" },
  { es: "Sábado", en: "Saturday" },
];

/** "miércoles 21/10" for an ISO date (read at noon UTC, so no timezone moves it a day). */
export function dayLabel(isoDate: string, locale: "es" | "en" = "es"): string {
  const d = new Date(`${isoDate}T12:00:00Z`);
  const [, m, day] = isoDate.split("-");
  return `${WEEKDAY_LABEL[d.getUTCDay()][locale].toLowerCase()} ${locale === "es" ? `${day}/${m}` : `${m}/${day}`}`;
}

export function parseTransferInfo(raw: unknown): TransferInfo | null {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const t = { holder: str(o.holder, 120), taxId: str(o.taxId, 20), bank: str(o.bank, 80), cbu: str(o.cbu, 30).replace(/\s/g, ""), alias: str(o.alias, 40) };
  return t.holder && (t.cbu || t.alias) ? t : null;
}

export function parseWalletList(raw: unknown): WalletInfo[] {
  return (Array.isArray(raw) ? raw : [])
    .map((w) => {
      const o = (w && typeof w === "object" ? w : {}) as Record<string, unknown>;
      return { asset: str(o.asset, 12) || "USDT", network: str(o.network, 40), address: str(o.address, 120) };
    })
    .filter((w) => w.network && /^[A-Za-z0-9:_-]{10,120}$/.test(w.address))
    .slice(0, 4);
}

export interface SellerStats {
  sales: number;
  ratingAvg: number | null;
  ratingCount: number;
  /** Last 90 days. */
  responseMinutes: number | null;
  cancelPct: number | null;
  onTimePct: number | null;
}

export type SellerBadge = "approved" | "company" | "verified" | "vmax" | "ex" | "founder" | "new" | "limited" | "sales_50" | "top";

export function sellerBadges(s: Pick<SellerStats, "sales" | "ratingAvg" | "ratingCount">, opts: { founder?: boolean; isNew?: boolean; limited?: boolean; verified?: boolean; plan?: SellerPlan; company?: boolean } = {}): SellerBadge[] {
  const out: SellerBadge[] = ["approved"];
  if (opts.company) out.push("company");
  if (opts.verified) out.push("verified");
  if (opts.plan === "vmax" || opts.plan === "ex") out.push(opts.plan);
  if (opts.founder) out.push("founder");
  if (opts.isNew) out.push("new");
  if (opts.limited) out.push("limited");
  if (s.sales >= 50) out.push("sales_50");
  if (s.ratingAvg !== null && s.ratingAvg >= 4.8 && s.ratingCount >= 20) out.push("top");
  return out;
}

/** Shipped within this many days of being paid counts as on time. */
export const ON_TIME_DAYS = 3;

/** Badges: market orders and offers waiting for the user (each order counted once). */
export interface MarketInbox {
  /** Purchases to pay or to confirm received, or with unread messages. */
  purchases: number;
  /** Counter-offers to answer and accepted offers to buy. */
  buyerOffers: number;
  /** Sales to confirm or send, or with unread messages. */
  sales: number;
  /** Offers waiting for the seller's answer. */
  sellerOffers: number;
  total: number;
  /** Has a shop (any state but turned down): the menus show "Mi tienda". */
  shop: boolean;
}

/** A shop found by the global search, with how many listings it has for sale. */
export interface SellerMatch extends SellerSummary {
  listings: number;
}

export interface SellerSummary {
  handle: string;
  displayName: string;
  avatarUrl: string | null;
  city: string;
  province: string;
  sales: number;
  ratingAvg: number | null;
  ratingCount: number;
  /** New seller (isNewSeller): buyers see a "Vendedor nuevo" tag. */
  isNew?: boolean;
  /** The admin vouched for them (sellers.vetted_at): buyers see "Verificado". */
  verified?: boolean;
  /** Approved with limits (sellers.limited_at): only cash, handed over in person. */
  limited?: boolean;
}

export interface SellerPublic extends SellerSummary {
  bio: string;
  memberSince: string;
  sellerSince: string;
  avatarLevel: number | null;
  stats: SellerStats;
  badges: SellerBadge[];
  ratingDistribution: Record<1 | 2 | 3 | 4 | 5, number>;
  delivery: DeliveryOptions;
  payments: MarketPaymentMethod[];
  acceptsOffers: boolean;
  vacationUntil: string | null;
  followers: number;
  following: boolean;
  /** The viewer's own shop: its lists have no cart buttons (checkout refuses one's own cards). */
  mine?: boolean;
  /** New seller: orders above this can only be paid with SAFE_PAYMENTS (null: no limit). */
  safePayAboveArs: number | null;
  /** The shop's minimum purchase: an order's cards (before a coupon, without delivery) add up to at least this (null: none). */
  minOrderArs: number | null;
  /** A company's shop: its legal name and CUIT (11 digits), shown to buyers. */
  company: { legalName: string; cuit: string } | null;
  /** Customized shop (banner, "Sobre la tienda", links), while the plan includes it. */
  shop: ShopContent | null;
  /** "Destacadas en mi tienda": the listings the shop puts first, in its order, while the plan includes it. */
  pins?: ListingCard[];
  /** The shop's coupon for everyone, when it has a live one. */
  coupon?: PublicCoupon | null;
  /** The shop's announcement (VMAX), while it holds. */
  announcement?: ShopAnnouncement | null;
  /** The shop's own sections (VMAX), those with cards now. */
  sections?: ShopSectionView[];
  /** Where the banner leads (VMAX), while it's there. */
  bannerTarget?: ShopBannerTarget | null;
}

export interface ReviewView {
  orderNumber: string;
  stars: number;
  body: string;
  reply: string | null;
  createdAt: string;
  author: { name: string; avatarUrl: string | null };
}

export interface ListingCard {
  id: number;
  productId: number;
  productSlug: string;
  /** The card's game (the finishes it can have, shared/games.ts). */
  game?: string;
  name: string;
  setName: string | null;
  cardNumber: string | null;
  rarity: string | null;
  /** Catalog category ("singles", "etb", "booster-box"…). */
  category: string;
  condition: ListingCondition;
  language: string;
  finish: MarketFinish;
  grader: Grader | null;
  grade: number | null;
  priceArs: number;
  quantity: number;
  photo: string | null;
  /** False: no photos of its own, the catalog image is shown. */
  ownPhotos: boolean;
  /** The seller's photos of this copy, in order (the gallery the list opens from 📷). */
  photos?: string[];
  catalogImage: string;
  /** The product isn't out yet (the catalog's pre-sale flag): labelled "Preventa". */
  presale?: boolean;
  releaseDate?: string | null;
  createdAt: string;
  seller: SellerSummary;
  /** The whole market's list (one card per product): how many listings and sellers offer it under the filters; this is the cheapest. */
  offers?: { listings: number; sellers: number };
}

/**
 * What a card sells for, next to the price when listing it: other shops' live listings of the same
 * product and condition (ungraded), or else another shop's last sale on pokefans; null when there's neither.
 */
export type PriceReference =
  | { source: "market"; medianArs: number; minArs: number; sellers: number }
  | { source: "sale"; lastSaleArs: number; soldAt: string };

/** Sets in the header's "Comprar" panel. */
export const NAV_SETS = 8;

/** The header's "Comprar" panel: what's on sale now (distinct cards), by category and by set. */
/** A set with something on sale now (the home's "Más colecciones"). */
export interface MarketSet {
  name: string;
  /** Products of the set on sale (cards and sealed). */
  products: number;
  /** A catalog image of the set (its priciest card on sale). */
  imageKey: string;
}

export interface MarketNav {
  categories: { slug: string; cards: number }[];
  sets: { name: string; cards: number }[];
  /** The set with the most cards on sale, with its priciest card's catalog image. */
  featured: { set: string; cards: number; imageKey: string } | null;
}

/** How an offer reaches the buyer, in short (addresses and hours are on the listing page). */
export interface OfferDelivery {
  /** Hand delivery zones (empty = no hand delivery) and its cost. */
  handZones: RatedZone[];
  handCostArs: number | null;
  ship: boolean;
  /** null: quoted in the chat. */
  shipCostArs: number | null;
  pickup: boolean;
}

/** The signed-in viewer, for the public lists: their own shop (null: none) and the listings they saved. */
export interface MarketViewer {
  handle: string | null;
  favorites: number[];
}

/**
 * Where a listing is seen: its offer on the card's page (/producto/<slug>?oferta=<id>, highlighted).
 * /mercado/<id> still works (old links): it goes there.
 */
export function listingPath(l: { id: number; productSlug: string }): string {
  return `/producto/${encodeURIComponent(l.productSlug)}?${OFFER_PARAM}=${l.id}`;
}
/** The card page's parameter naming the offer to show. */
export const OFFER_PARAM = "oferta";

/** A row of the offers table on a product page. */
export interface ProductOffer extends ListingCard {
  notes: string;
  /** The shop takes price offers ("Hacer una oferta"). */
  acceptsOffers: boolean;
  payments: MarketPaymentMethod[];
  delivery: OfferDelivery;
}

export interface ProductOffers {
  /** Every visible offer (the items are the cheapest, up to a cap). */
  count: number;
  minPriceArs: number | null;
  items: ProductOffer[];
  /** People looking for it on "Se busca" (the public board). */
  wanted?: number;
}

export interface ListingDetail extends ListingCard {
  notes: string;
  photos: string[];
  status: "active" | "paused" | "removed";
  sellerPayments: MarketPaymentMethod[];
  sellerDelivery: DeliveryOptions;
  acceptsOffers: boolean;
  vacationUntil: string | null;
  /** Reference: the store's price for this card (pesos) and other community listings. */
  reference: { storeArs: number | null; storeInStock: boolean; marketMinArs: number | null; marketCount: number };
  favorite: boolean;
  mine: boolean;
}

export interface MarketOrderLine {
  listingId: number;
  productId: number;
  title: string;
  condition: string;
  image: string | null;
  quantity: number;
  unitPriceArs: number;
}

export type OrderRole = "buyer" | "seller";

export interface MarketOrderView {
  number: string;
  role: OrderRole;
  status: MarketOrderStatus;
  paymentMethod: MarketPaymentMethod;
  deliveryMode: DeliveryMode;
  subtotalArs: number;
  /** The whole discount: the coupon's part, plus the shop's bulk discount on orders from before it was dropped. */
  discountArs: number;
  coupon: { code: string; discountArs: number } | null;
  deliveryCostArs: number | null;
  totalArs: number;
  totalUsdCents: number | null;
  lines: MarketOrderLine[];
  seller: SellerSummary;
  buyer: { name: string; avatarUrl: string | null; phone: string | null; email: string | null; address: string; city: string; notes: string; ratingAvg: number | null; ratingCount: number };
  /** Payment details for the buyer while unpaid (transfer / wallets); contact once paid. */
  payTo: { transfer: TransferInfo | null; wallets: WalletInfo[] } | null;
  sellerContact: { name: string; phone: string; email: string } | null;
  /** Day and time window picked for an in-person delivery. */
  meeting: { date: string; from: string; to: string } | null;
  pickup: { address: string; hours: string } | null;
  tracking: string | null;
  buyerMarkedPaidAt: string | null;
  /** Crypto on a chain: the exact USDT to send ("13.340173"), its last decimals only this order's. */
  payUsdt: string | null;
  /**
   * Crypto: the transaction hash the buyer gave, its explorer page (null on a network without one) and,
   * on TRON, what the chain said of it (null: not checked).
   */
  paymentTx: { hash: string; network: string; url: string | null; check: TxCheck | null } | null;
  paidAt: string | null;
  shippedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  cancelledBy: string | null;
  cancelReason: string | null;
  disputeReason: string | null;
  reservedUntil: string | null;
  autoCompleteAt: string | null;
  createdAt: string;
  messages: { id: number; mine: boolean; system: boolean; body: string; createdAt: string }[];
  myReview: { stars: number; body: string } | null;
  theirReview: { stars: number; body: string; reply: string | null } | null;
  actions: OrderAction[];
}

export type OrderAction = "pay_mp" | "mark_paid" | "confirm_payment" | "ship" | "handed" | "received" | "cancel" | "dispute" | "review" | "reply";

/** What each side can do now (the server enforces the same rules). */
export function orderActions(o: { status: MarketOrderStatus; paymentMethod: MarketPaymentMethod; buyerMarkedPaidAt: string | null }, role: OrderRole, reviewed: { mine: boolean; replyPending: boolean }): OrderAction[] {
  const a: OrderAction[] = [];
  if (o.status === "pending_payment") {
    if (role === "buyer") {
      if (o.paymentMethod === "mercadopago") a.push("pay_mp");
      else if (!o.buyerMarkedPaidAt) a.push("mark_paid");
      a.push("cancel");
    } else {
      if (o.paymentMethod !== "mercadopago") a.push("confirm_payment");
      a.push("cancel");
    }
  }
  if (o.status === "confirmed") {
    // Cash: paid at the handover.
    if (role === "seller") a.push("handed", "cancel");
    else a.push("received", "cancel", "dispute");
  }
  if (o.status === "paid") {
    if (role === "seller") a.push("ship", "cancel");
    else a.push("received", "dispute");
  }
  if (o.status === "shipped" && role === "buyer") a.push("received", "dispute");
  if (o.status === "completed" && !reviewed.mine) a.push("review");
  if (reviewed.replyPending) a.push("reply");
  return a;
}

export interface OfferView {
  id: number;
  listingId: number;
  listingName: string;
  photo: string | null;
  listPriceArs: number;
  quantity: number;
  priceArs: number;
  counterArs: number | null;
  status: OfferStatus;
  expiresAt: string;
  createdAt: string;
  role: OrderRole;
  other: { name: string; handle: string | null };
  /** The seller's side: how many have the listing in favorites, and whether it's featured now. */
  listingFavorites?: number;
  listingFeatured?: boolean;
  /** Came from the seller's "Reservar para un cliente" link, not from the buyer's offer. */
  fromReservation?: boolean;
}

export const MARKET_SORTS = ["newest", "price_asc", "price_desc", "rating"] as const;
export type MarketSort = (typeof MARKET_SORTS)[number];

export interface MarketFilters {
  q: string;
  product: number | null;
  seller: string | null;
  /** Catalog categories ("singles", "etb"…). */
  categories: string[];
  sets: string[];
  rarities: string[];
  conditions: MarketCondition[];
  languages: string[];
  finish: MarketFinish | null;
  graded: boolean | null;
  minPrice: number | null;
  maxPrice: number | null;
  minRating: number | null;
  minSales: number | null;
  zone: RatedZone | null;
  payment: MarketPaymentMethod | null;
  sort: MarketSort;
  page: number;
  /** The visitor's game (`juego`, src/lib/GameProvider.tsx): the market never mixes games; none means the first open one. */
  game?: string | null;
}

const list = (sp: URLSearchParams, key: string) =>
  [...new Set(sp.getAll(key).flatMap((v) => v.split(",")).map((v) => v.trim()).filter(Boolean))].slice(0, 20);
const num = (v: string | null, min: number, max: number) => {
  if (v === null || v.trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= min && n <= max ? n : null;
};

/** Query string → filters; the same parser runs in the browser and the API. */
export function parseMarketFilters(sp: URLSearchParams): MarketFilters {
  const product = num(sp.get("producto"), 1, 2 ** 31 - 1);
  const seller = sp.get("vendedor")?.trim().toLowerCase() ?? "";
  const graded = sp.get("gradeada");
  const zone = sp.get("zona");
  const payment = sp.get("pago");
  const sort = sp.get("orden");
  const finish = sp.get("acabado");
  return {
    q: (sp.get("q") ?? "").trim().slice(0, 80),
    product: product !== null && Number.isInteger(product) ? product : null,
    seller: HANDLE_RE.test(seller) ? seller : null,
    categories: list(sp, "categoria").filter((c) => /^[a-z0-9-]{1,40}$/.test(c)),
    sets: list(sp, "set").map((s) => s.slice(0, 80)),
    rarities: list(sp, "rareza").map((s) => s.slice(0, 60)),
    conditions: list(sp, "estado").filter((c): c is MarketCondition => isOneOf(MARKET_CONDITIONS, c)),
    languages: list(sp, "idioma").filter((l) => isOneOf(MARKET_LANGUAGES, l)),
    finish: isOneOf(MARKET_FINISHES, finish) ? finish : null,
    graded: graded === "1" ? true : graded === "0" ? false : null,
    minPrice: num(sp.get("min"), 0, 100_000_000),
    maxPrice: num(sp.get("max"), 0, 100_000_000),
    minRating: num(sp.get("estrellas"), 1, 5),
    minSales: num(sp.get("ventas"), 1, 10_000),
    zone: isOneOf(RATED_ZONES, zone) ? zone : null,
    payment: isOneOf(MARKET_PAYMENT_METHODS, payment) ? payment : null,
    sort: isOneOf(MARKET_SORTS, sort) ? sort : "newest",
    page: Math.min(Math.max(1, Math.floor(num(sp.get("pagina"), 1, 1000) ?? 1)), 1000),
    game: gameSlug(sp.get(GAME_PARAM)),
  };
}

export function marketFiltersToQuery(f: Partial<MarketFilters>): string {
  const sp = new URLSearchParams();
  if (f.q) sp.set("q", f.q);
  if (f.product) sp.set("producto", String(f.product));
  if (f.seller) sp.set("vendedor", f.seller);
  if (f.categories?.length) sp.set("categoria", f.categories.join(","));
  for (const s of f.sets ?? []) sp.append("set", s);
  for (const r of f.rarities ?? []) sp.append("rareza", r);
  if (f.conditions?.length) sp.set("estado", f.conditions.join(","));
  if (f.languages?.length) sp.set("idioma", f.languages.join(","));
  if (f.finish) sp.set("acabado", f.finish);
  if (f.graded !== null && f.graded !== undefined) sp.set("gradeada", f.graded ? "1" : "0");
  if (f.minPrice != null) sp.set("min", String(f.minPrice));
  if (f.maxPrice != null) sp.set("max", String(f.maxPrice));
  if (f.minRating != null) sp.set("estrellas", String(f.minRating));
  if (f.minSales != null) sp.set("ventas", String(f.minSales));
  if (f.zone) sp.set("zona", f.zone);
  if (f.payment) sp.set("pago", f.payment);
  if (f.sort && f.sort !== "newest") sp.set("orden", f.sort);
  if (f.page && f.page > 1) sp.set("pagina", String(f.page));
  if (f.game) sp.set(GAME_PARAM, f.game);
  return sp.toString();
}

/**
 * The old store catalog's address (/tienda?category=…&set=…&rarity=…&q=…) as the market's: links in
 * emails, banners and search engines keep working. What the market has no filter for is dropped.
 */
export function marketPathFromCatalog(search: string): string {
  const sp = new URLSearchParams(search);
  const query = marketFiltersToQuery({
    q: (sp.get("q") ?? "").trim().slice(0, 80),
    categories: (sp.get("category") ?? "").split(",").map((c) => c.trim()).filter((c) => /^[a-z0-9-]{1,40}$/.test(c)),
    sets: sp.getAll("set").map((v) => v.trim()).filter(Boolean),
    rarities: sp.getAll("rarity").map((v) => v.trim()).filter(Boolean),
  });
  return query ? `/mercado?${query}` : "/mercado";
}

export const ORDER_STATUS_LABEL: Record<MarketOrderStatus, { es: string; en: string }> = {
  pending_payment: { es: "Esperando el pago", en: "Awaiting payment" },
  confirmed: { es: "Confirmado (se paga al entregar)", en: "Confirmed (pay on delivery)" },
  paid: { es: "Pagado", en: "Paid" },
  shipped: { es: "Enviado / entregado", en: "Sent / handed over" },
  completed: { es: "Completado", en: "Completed" },
  cancelled: { es: "Cancelado", en: "Cancelled" },
  disputed: { es: "En revisión", en: "Under review" },
};

export const PAYMENT_LABEL: Record<MarketPaymentMethod, { es: string; en: string }> = {
  mercadopago: { es: "Mercado Pago", en: "Mercado Pago" },
  transfer: { es: "Transferencia", en: "Bank transfer" },
  crypto: { es: "Cripto (USDT)", en: "Crypto (USDT)" },
  cash: { es: "Efectivo contra entrega", en: "Cash on delivery" },
};

export const DELIVERY_LABEL: Record<DeliveryMode, { es: string; en: string }> = {
  hand: { es: "Entrega en mano", en: "Hand delivery" },
  ship: { es: "Envío del vendedor", en: "Shipped by the seller" },
  pickup: { es: "Punto de retiro", en: "Pickup point" },
  arrange: { es: "A convenir", en: "To be agreed" },
};

export const REPORT_REASON_LABEL: Record<ReportReason, { es: string; en: string }> = {
  fake: { es: "Carta falsa o proxy", en: "Fake card or proxy" },
  wrong_item: { es: "No es lo publicado", en: "Not as described" },
  not_delivered: { es: "No me la entregó", en: "Not delivered" },
  not_paid: { es: "No me pagó", en: "Not paid" },
  abuse: { es: "Trato abusivo", en: "Abusive behaviour" },
  spam: { es: "Spam", en: "Spam" },
  other: { es: "Otro", en: "Other" },
};

export const BADGE_LABEL: Record<SellerBadge, { es: string; en: string; icon: string }> = {
  approved: { es: "Vendedor aprobado", en: "Approved seller", icon: "✅" },
  company: { es: "Empresa", en: "Company", icon: "🏢" },
  verified: { es: "Verificado", en: "Verified", icon: "🪪" },
  vmax: { es: "VMAX", en: "VMAX", icon: "🔥" },
  ex: { es: "EX", en: "EX", icon: "💎" },
  founder: { es: "Vendedor fundador", en: "Founding seller", icon: "🏛️" },
  new: { es: "Vendedor nuevo", en: "New seller", icon: "🆕" },
  limited: { es: "Solo efectivo en mano", en: "Cash in person only", icon: "🤝" },
  sales_50: { es: "+50 ventas", en: "50+ sales", icon: "🏅" },
  top: { es: "Top vendedor", en: "Top seller", icon: "⭐" },
};
